// Typescript CodeGen Prototype "Expectant Sunshine"
// Transform Version: v1.0.0
// Template Version:  v1.0.0
//
// -- DO NOT MODIFY THIS FILE! --

/* tslint:disable:no-trailing-whitespace */

import * as _ from 'lodash';
import * as Wellspring from '@greenheck/wellspring';
import * as Axon from '@jlekie/axon';
import { v4 as Uuid } from 'uuid';
import * as ParseHelpers from '@jlekie/parse-helpers';
import * as Zod from 'zod';

export enum QuestionStates {
    Unassigned,
    SystemAssigned,
    UserAssigned
}

export function isEntityModel(source: any): source is IEntity {
    // Method Implementation: simple
    return source.modelName === 'Entity';
}

export function isQuestionModel(source: any): source is IQuestion {
    // Method Implementation: simple
    return source.modelName === 'Question';
}

export interface EntityZodSchemaShape {
    entityId?: string;

    questions?: QuestionZodSchemaShape[];

    children?: EntityZodSchemaShape[];
}

export interface QuestionZodSchemaShape {
    name?: string;

    value?: string;

    important?: boolean;

    state?: 'Unassigned' | 'SystemAssigned' | 'UserAssigned';
}

// Artifact Implementation: artifacts/zodSchemaType
export const EntityZodSchema: Zod.ZodSchema<EntityZodSchemaShape> = Zod.lazy(() => Zod.object({
    entityId: Zod.string().optional(),
    questions: Zod.array(QuestionZodSchema).optional(),
    children: Zod.array(EntityZodSchema).optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const QuestionZodSchema: Zod.ZodSchema<QuestionZodSchemaShape> = Zod.lazy(() => Zod.object({
    name: Zod.string().optional(),
    value: Zod.string().optional(),
    important: Zod.boolean().optional(),
    state: Zod.enum([ 'Unassigned', 'SystemAssigned', 'UserAssigned' ]).optional()
}).nonstrict());

export interface IEntity extends Wellspring.IModel {
    readonly modelName: string;

    readonly entityId: string;

    readonly questions: Question[];

    readonly children: Entity[];

    readonly isEntityIdDefined: boolean;

    readonly isQuestionsDefined: boolean;

    readonly isChildrenDefined: boolean;
}

export interface IQuestion extends Wellspring.IModel {
    readonly modelName: string;

    readonly name: string;

    readonly value: string;

    readonly important: boolean;

    readonly state: QuestionStates;

    readonly isNameDefined: boolean;

    readonly isValueDefined: boolean;

    readonly isImportantDefined: boolean;

    readonly isStateDefined: boolean;
}

export class Entity implements IEntity {
    public static readonly modelName: string = 'Entity';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'entityId', 'questions', 'children' ];

    public get modelName(): string {
        // Method Implementation: simple
        return Entity.modelName;
    }

    private _entityId: string;

    private _questions: Question[];

    private _children: Entity[];

    public get entityId(): string {
        // Method Implementation: simple
        return this._entityId;
    }

    public get questions(): Question[] {
        // Method Implementation: simple
        return this._questions;
    }

    public get children(): Entity[] {
        // Method Implementation: simple
        return this._children;
    }

    public set entityId(value: string) {
        // Method Implementation: simple
        this._entityId = value;
        this._isEntityIdDefined = true;
    }

    public set questions(value: Question[]) {
        // Method Implementation: simple
        this._questions = value;
        this._isQuestionsDefined = true;
    }

    public set children(value: Entity[]) {
        // Method Implementation: simple
        this._children = value;
        this._isChildrenDefined = true;
    }

    private _isEntityIdDefined: boolean;

    private _isQuestionsDefined: boolean;

    private _isChildrenDefined: boolean;

    public get isEntityIdDefined(): boolean {
        // Method Implementation: simple
        return this._isEntityIdDefined;
    }

    public get isQuestionsDefined(): boolean {
        // Method Implementation: simple
        return this._isQuestionsDefined;
    }

    public get isChildrenDefined(): boolean {
        // Method Implementation: simple
        return this._isChildrenDefined;
    }

    public constructor();
    public constructor(source: IEntity);
    public constructor(source: Partial<Pick<Entity, 'entityId' | 'questions' | 'children'>>);
    public constructor(source?: IEntity | Partial<Pick<Entity, 'entityId' | 'questions' | 'children'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isEntityModel(source)) {
                if (source.isEntityIdDefined) {
                    this._entityId = source.entityId;
                    this._isEntityIdDefined = true;
                }
                else {
                    this._entityId = '';
                    this._isEntityIdDefined = false;
                }
                
                if (source.isQuestionsDefined) {
                    this._questions = source.questions;
                    this._isQuestionsDefined = true;
                }
                else {
                    this._questions = [];
                    this._isQuestionsDefined = true;
                }
                
                if (source.isChildrenDefined) {
                    this._children = source.children;
                    this._isChildrenDefined = true;
                }
                else {
                    this._children = [];
                    this._isChildrenDefined = true;
                }
            }
            else {
                if (source.entityId !== undefined) {
                    this._entityId = source.entityId;
                    this._isEntityIdDefined = true;
                }
                else {
                    this._entityId = '';
                    this._isEntityIdDefined = false;
                }
                
                if (source.questions !== undefined) {
                    this._questions = source.questions;
                    this._isQuestionsDefined = true;
                }
                else {
                    this._questions = [];
                    this._isQuestionsDefined = true;
                }
                
                if (source.children !== undefined) {
                    this._children = source.children;
                    this._isChildrenDefined = true;
                }
                else {
                    this._children = [];
                    this._isChildrenDefined = true;
                }
            }
        }
        else {
            this._entityId = '';
            this._isEntityIdDefined = false;
        
            this._questions = [];
            this._isQuestionsDefined = true;
        
            this._children = [];
            this._isChildrenDefined = true;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return Entity.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isEntityIdDefined && this.entityId !== undefined)
            definedProperties.push('entityId');
        if (this.isQuestionsDefined && this.questions !== undefined)
            definedProperties.push('questions');
        if (this.isChildrenDefined && this.children !== undefined)
            definedProperties.push('children');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'entityId':
                return this.entityId;
            case 'questions':
                return this.questions;
            case 'children':
                return this.children;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isEntityIdDefined && this.entityId !== undefined) {
            if (this.entityId !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('entityId', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.entityId);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('entityId', 'null'));
            }
        }
        
        if (this.isQuestionsDefined && this.questions !== undefined) {
            if (this.questions !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('questions', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.questions.length));
                for (let i = 0; i < this.questions.length; i++) {
                    const item = this.questions[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('questions', 'null'));
            }
        }
        
        if (this.isChildrenDefined && this.children !== undefined) {
            if (this.children !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('children', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.children.length));
                for (let i = 0; i < this.children.length; i++) {
                    const item = this.children[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('children', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'entityId':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.entityId = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'entityId' cannot be null`);
                    this.entityId = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'entityId'`);
                }
        
                return true;
            case 'questions':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.questions = [];
                
                    let item: Question = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === Question.modelName) {
                                item = new Question();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${Question.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'questions' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'questions'`);
                        }
                
                        this.questions.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'questions' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'questions'`);
                }
        
                return true;
            case 'children':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.children = [];
                
                    let item: Entity = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === Entity.modelName) {
                                item = new Entity();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${Entity.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'children' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'children'`);
                        }
                
                        this.children.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'children' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'children'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // entityId
        if (this.isEntityIdDefined && this.entityId !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.entityId === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.entityId);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // questions
        if (this.isQuestionsDefined && this.questions !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.questions === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.questions.length);
            
                for (const item of this.questions) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // children
        if (this.isChildrenDefined && this.children !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.children === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.children.length);
            
                for (const item of this.children) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // entityId
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.entityId = <any>null;
                    break;
                case 1:
                    this.entityId = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // questions
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: Question;
            
                    this.questions = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new Question();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.questions.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // children
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: Entity;
            
                    this.children = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new Entity();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.children.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): Entity {
        // Method Implementation: simple
        const schema = EntityZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: EntityZodSchemaShape): Entity {
        // Method Implementation: parseZodSchema
        return new Entity({
            entityId: schema.entityId,
            questions: schema.questions?.map(i => Question.parseZodSchema(i)),
            children: schema.children?.map(i => Entity.parseZodSchema(i))
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            entityId: this.isEntityIdDefined ? ((this.entityId === null ? undefined : this.entityId)) : undefined,
            questions: this.isQuestionsDefined ? (this.questions?.map(item => item)) : undefined,
            children: this.isChildrenDefined ? (this.children?.map(item => item)) : undefined,
        };
    }

    public clone(): Entity {
        // Method Implementation: clone
        return new Entity({
            entityId: this.isEntityIdDefined ? this.entityId : undefined,
            questions: this.isQuestionsDefined ? this.questions?.map(i => i.clone()) : undefined,
            children: this.isChildrenDefined ? this.children?.map(i => i.clone()) : undefined
        });
    }
}

export class Question implements IQuestion {
    public static readonly modelName: string = 'Question';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'name', 'value', 'important', 'state' ];

    public get modelName(): string {
        // Method Implementation: simple
        return Question.modelName;
    }

    private _name: string;

    private _value: string;

    private _important: boolean;

    private _state: QuestionStates;

    public get name(): string {
        // Method Implementation: simple
        return this._name;
    }

    public get value(): string {
        // Method Implementation: simple
        return this._value;
    }

    public get important(): boolean {
        // Method Implementation: simple
        return this._important;
    }

    public get state(): QuestionStates {
        // Method Implementation: simple
        return this._state;
    }

    public set name(value: string) {
        // Method Implementation: simple
        this._name = value;
        this._isNameDefined = true;
    }

    public set value(value: string) {
        // Method Implementation: simple
        this._value = value;
        this._isValueDefined = true;
    }

    public set important(value: boolean) {
        // Method Implementation: simple
        this._important = value;
        this._isImportantDefined = true;
    }

    public set state(value: QuestionStates) {
        // Method Implementation: simple
        this._state = value;
        this._isStateDefined = true;
    }

    private _isNameDefined: boolean;

    private _isValueDefined: boolean;

    private _isImportantDefined: boolean;

    private _isStateDefined: boolean;

    public get isNameDefined(): boolean {
        // Method Implementation: simple
        return this._isNameDefined;
    }

    public get isValueDefined(): boolean {
        // Method Implementation: simple
        return this._isValueDefined;
    }

    public get isImportantDefined(): boolean {
        // Method Implementation: simple
        return this._isImportantDefined;
    }

    public get isStateDefined(): boolean {
        // Method Implementation: simple
        return this._isStateDefined;
    }

    public constructor();
    public constructor(source: IQuestion);
    public constructor(source: Partial<Pick<Question, 'name' | 'value' | 'important' | 'state'>>);
    public constructor(source?: IQuestion | Partial<Pick<Question, 'name' | 'value' | 'important' | 'state'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isQuestionModel(source)) {
                if (source.isNameDefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.isValueDefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = '';
                    this._isValueDefined = false;
                }
                
                if (source.isImportantDefined) {
                    this._important = source.important;
                    this._isImportantDefined = true;
                }
                else {
                    this._important = false;
                    this._isImportantDefined = false;
                }
                
                if (source.isStateDefined) {
                    this._state = source.state;
                    this._isStateDefined = true;
                }
                else {
                    this._state = QuestionStates.Unassigned;
                    this._isStateDefined = false;
                }
            }
            else {
                if (source.name !== undefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.value !== undefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = '';
                    this._isValueDefined = false;
                }
                
                if (source.important !== undefined) {
                    this._important = source.important;
                    this._isImportantDefined = true;
                }
                else {
                    this._important = false;
                    this._isImportantDefined = false;
                }
                
                if (source.state !== undefined) {
                    this._state = source.state;
                    this._isStateDefined = true;
                }
                else {
                    this._state = QuestionStates.Unassigned;
                    this._isStateDefined = false;
                }
            }
        }
        else {
            this._name = '';
            this._isNameDefined = false;
        
            this._value = '';
            this._isValueDefined = false;
        
            this._important = false;
            this._isImportantDefined = false;
        
            this._state = QuestionStates.Unassigned;
            this._isStateDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return Question.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isNameDefined && this.name !== undefined)
            definedProperties.push('name');
        if (this.isValueDefined && this.value !== undefined)
            definedProperties.push('value');
        if (this.isImportantDefined && this.important !== undefined)
            definedProperties.push('important');
        if (this.isStateDefined && this.state !== undefined)
            definedProperties.push('state');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'name':
                return this.name;
            case 'value':
                return this.value;
            case 'important':
                return this.important;
            case 'state':
                return this.state;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isNameDefined && this.name !== undefined) {
            if (this.name !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.name);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'null'));
            }
        }
        
        if (this.isValueDefined && this.value !== undefined) {
            if (this.value !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.value);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'null'));
            }
        }
        
        if (this.isImportantDefined && this.important !== undefined) {
            if (this.important !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('important', 'boolean'));
        
                // Implementation: axon/write/boolean
                writer.writeBooleanValue(this.important);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('important', 'null'));
            }
        }
        
        if (this.isStateDefined && this.state !== undefined) {
            if (this.state !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('state', 'enum'));
        
                // Implementation: axon/write/enum
                writer.writeEnumValue(this.state);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('state', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'name':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.name = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'name' cannot be null`);
                    this.name = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'name'`);
                }
        
                return true;
            case 'value':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.value = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'value' cannot be null`);
                    this.value = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'value'`);
                }
        
                return true;
            case 'important':
                // Implementation: axon/readProperty/boolean
                if (propertyHeader.type === 'boolean') {
                    this.important = reader.readBooleanValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'important' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'important'`);
                }
        
                return true;
            case 'state':
                // Implementation: axon/readProperty/enum
                if (propertyHeader.type === 'enum') {
                    this.state = reader.readEnumValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Property 'state' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'state'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // name
        if (this.isNameDefined && this.name !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.name === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.name);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // value
        if (this.isValueDefined && this.value !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.value === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.value);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // important
        if (this.isImportantDefined && this.important !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/boolean
            writer.writeBooleanValue(this.important);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // state
        if (this.isStateDefined && this.state !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/enum
            writer.writeEnumValue(this.state);
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // name
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.name = <any>null;
                    break;
                case 1:
                    this.name = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // value
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.value = <any>null;
                    break;
                case 1:
                    this.value = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // important
        if (reader.readBooleanValue())
        {
            // Implementation: boolean
            this.important = reader.readBooleanValue();
        }
        
        // state
        if (reader.readBooleanValue())
        {
            // Implementation: enum
            this.state = reader.readEnumValue();
        }
    }

    public static parse(hash: unknown): Question {
        // Method Implementation: simple
        const schema = QuestionZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: QuestionZodSchemaShape): Question {
        // Method Implementation: parseZodSchema
        return new Question({
            name: schema.name,
            value: schema.value,
            important: schema.important,
            state: (() => {
            if (!schema.state)
                return;
        
            switch (schema.state) {
                case 'Unassigned':
                    return QuestionStates.Unassigned;
                case 'SystemAssigned':
                    return QuestionStates.SystemAssigned;
                case 'UserAssigned':
                    return QuestionStates.UserAssigned;
                default:
                    throw new Error(`Enum type "schema.state" not recognized`);
            }
        })()
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            name: this.isNameDefined ? ((this.name === null ? undefined : this.name)) : undefined,
            value: this.isValueDefined ? ((this.value === null ? undefined : this.value)) : undefined,
            important: this.isImportantDefined ? (this.important) : undefined,
            state: this.isStateDefined ? (QuestionStates[this.state]) : undefined,
        };
    }

    public clone(): Question {
        // Method Implementation: clone
        return new Question({
            name: this.isNameDefined ? this.name : undefined,
            value: this.isValueDefined ? this.value : undefined,
            important: this.isImportantDefined ? this.important : undefined,
            state: this.isStateDefined ? this.state : undefined
        });
    }
}

export class ModelClassFactory {
    public static createModel(modelName: string) {
        // Method Implementation: createModel
        switch (modelName) {
            case 'Entity':
                return new Entity();
            case 'Question':
                return new Question();
            default:
                throw new Error(`Unknown model ${modelName}`);
        }
    }

    public static createFromModel(modelName: string, source: any) {
        // Method Implementation: createFromModel
        switch (modelName) {
            case 'Entity':
                return new Entity(source);
            case 'Question':
                return new Question(source);
            default:
                throw new Error(`Unknown model ${modelName}`);
        }
    }

    public static parseModel(modelName: string, source: any) {
        // Method Implementation: parseModel
        switch (modelName) {
            case 'Entity':
                return Entity.parse(source);
            case 'Question':
                return Question.parse(source);
            default:
                throw new Error(`Unknown model ${modelName}`);
        }
    }
}
